const BUSINESS_TIME_ZONE = 'America/Argentina/Buenos_Aires';

function extractDateKeyPrefix(raw) {
    const match = String(raw).trim().match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : null;
}

function compareDateKeys(left, right) {
    if (!left || !right) {
        return 0;
    }

    if (left === right) {
        return 0;
    }

    return left < right ? -1 : 1;
}

function formatDateKeyInBusinessTz(date) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: BUSINESS_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date);

    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;

    if (!year || !month || !day) {
        return null;
    }

    return `${year}-${month}-${day}`;
}

function getBusinessDateKey(value) {
    if (!value) {
        return null;
    }

    const raw = String(value).trim();
    if (!raw) {
        return null;
    }

    const prefix = extractDateKeyPrefix(raw);
    if (prefix) {
        return prefix;
    }

    // Si llega como fecha SQL sin zona, tomar la parte de fecha literal
    // para evitar que dependa del huso horario del navegador.
    const sqlMatch = raw.match(/^(\d{4}-\d{2}-\d{2})(?:[ T].*)?$/);
    if (sqlMatch && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw)) {
        return sqlMatch[1];
    }

    const parsed = parseDate(raw);
    if (!parsed) {
        return null;
    }

    return formatDateKeyInBusinessTz(parsed);
}

export function formatDate(value) {
    const dateKey = value !== null && value !== undefined
        ? extractDateKeyPrefix(value)
        : null;

    if (dateKey) {
        const match = dateKey.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (match) {
            const year = Number(match[1]);
            const month = Number(match[2]) - 1;
            const day = Number(match[3]);
            // Mediodía local para evitar corrimientos en límites de zona.
            const localMidday = new Date(year, month, day, 12, 0, 0);

            if (!Number.isNaN(localMidday.getTime())) {
                try {
                    return new Intl.DateTimeFormat('es-AR', {
                        dateStyle: 'short',
                    }).format(localMidday);
                } catch {
                    return dateKey;
                }
            }
        }
    }

    const parsed = parseDate(value);
    if (!parsed) {
        return '-';
    }

    try {
        return new Intl.DateTimeFormat('es-AR', {
            timeZone: BUSINESS_TIME_ZONE,
            dateStyle: 'short',
        }).format(parsed);
    } catch {
        return String(value);
    }
}

export function mapStatusLabel(estado) {
    const labels = {
        pendiente: 'Pendiente',
        iniciada: 'Iniciada',
        finalizada: 'Finalizada',
        revisada: 'Revisada',
        aprobada: 'Aprobada',
        desaprobada: 'Desaprobada',
        cancelada: 'Cancelada',
    };

    return labels[estado] || estado || '-';
}

export function statusColor(estado) {
    const status = (estado || '').toLowerCase();

    if (status === 'aprobada' || status === 'finalizada') {
        return 'success';
    }

    if (status === 'iniciada' || status === 'revisada') {
        return 'info';
    }

    if (status === 'desaprobada' || status === 'cancelada') {
        return 'error';
    }

    return 'warning';
}

export const CLOSED_STATUSES = new Set([
    'finalizada',
    'aprobada',
    'desaprobada',
    'cancelada',
]);

export function parseDate(value) {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    const raw = String(value).trim();
    if (!raw) {
        return null;
    }

    // Formato SQL sin zona (YYYY-MM-DD HH:mm:ss): interpretarlo en hora local
    // para evitar corrimientos por UTC al inicio del día.
    const sqlMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
    if (sqlMatch && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw)) {
        const year = Number(sqlMatch[1]);
        const month = Number(sqlMatch[2]) - 1;
        const day = Number(sqlMatch[3]);
        const hours = Number(sqlMatch[4] || 0);
        const minutes = Number(sqlMatch[5] || 0);
        const seconds = Number(sqlMatch[6] || 0);
        const localDate = new Date(year, month, day, hours, minutes, seconds);
        return Number.isNaN(localDate.getTime()) ? null : localDate;
    }

    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function getTodayRange() {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return { start, end };
}

export function isTodayDate(value) {
    const visitDateKey = getBusinessDateKey(value);
    if (!visitDateKey) {
        return false;
    }

    const todayKey = formatDateKeyInBusinessTz(new Date());
    if (!todayKey) {
        return false;
    }

    return visitDateKey === todayKey;
}

export function isPendingOverdue(visita) {
    const status = String(visita?.estado || '').toLowerCase();
    if (status !== 'pendiente') {
        return false;
    }

    const visitDateKey = getBusinessDateKey(visita?.scheduled_at);
    if (!visitDateKey) {
        return false;
    }

    const todayKey = formatDateKeyInBusinessTz(new Date());
    if (!todayKey) {
        return false;
    }

    return compareDateKeys(visitDateKey, todayKey) < 0;
}

export function buildGoogleMapsLink(latitud, longitud) {
    if (latitud === null || latitud === undefined || longitud === null || longitud === undefined) {
        return null;
    }

    return `https://www.google.com/maps?q=${latitud},${longitud}`;
}

export function buildGoogleMapsEmbedUrl(latitud, longitud) {
    if (latitud === null || latitud === undefined || longitud === null || longitud === undefined) {
        return null;
    }

    return `https://www.google.com/maps?q=${latitud},${longitud}&z=16&output=embed`;
}

export function getStatusProgress(estado) {
    const normalized = String(estado || '').toLowerCase();

    if (normalized === 'iniciada') {
        return 1;
    }

    if (['finalizada', 'aprobada', 'desaprobada', 'cancelada', 'revisada'].includes(normalized)) {
        return 2;
    }

    return 0;
}

export function normalizeContactos(contactosRaw, contactoAdicionalRaw) {
    const asArray = (value) => {
        if (Array.isArray(value)) {
            return value;
        }

        if (value && typeof value === 'object') {
            return [value];
        }

        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                if (Array.isArray(parsed)) {
                    return parsed;
                }

                if (parsed && typeof parsed === 'object') {
                    return [parsed];
                }
            } catch {
                return [];
            }
        }

        return [];
    };

    const toKey = (item) => {
        return [
            item?.puesto,
            item?.nombre,
            item?.apellido,
            item?.email,
            item?.telefono_1,
            item?.telefono_2,
        ]
            .map((v) => (v ?? '').toString().trim().toLowerCase())
            .join('|');
    };

    const dedupe = (items) => {
        const seen = new Set();
        return items.filter((item) => {
            if (!item || typeof item !== 'object') {
                return false;
            }

            const key = toKey(item);
            if (seen.has(key)) {
                return false;
            }

            seen.add(key);
            return true;
        });
    };

    const contactos = dedupe(asArray(contactosRaw));
    if (contactos.length > 0) {
        return contactos;
    }

    return dedupe(asArray(contactoAdicionalRaw));
}

export function formatContactoNombre(contacto) {
    const nombre = [contacto?.nombre, contacto?.apellido]
        .filter(Boolean)
        .join(' ')
        .trim();

    return nombre || contacto?.nombre_completo || 'Sin nombre';
}

export function formatUsuarioNombreCompleto(user) {
    const nombre = String(user?.nombre || user?.first_name || '').trim();
    const apellido = String(user?.apellido || user?.last_name || '').trim();

    if (nombre || apellido) {
        return [nombre, apellido].filter(Boolean).join(' ');
    }

    const fullName = String(user?.name || '').trim();
    return fullName || 'el equipo técnico';
}

export function formatVehiculoLinea(vehiculo) {
    const patente = vehiculo?.patente || '-';
    const numeroInterno = vehiculo?.numero_interno
        ? `Vehículo: ${vehiculo.numero_interno}`
        : 'N° -';

    return `[${numeroInterno}] - Dominio: ${patente}`;
}

export function toDigits(value) {
    return String(value || '').replace(/\D+/g, '');
}

export function buildPhoneLink(phone) {
    const digits = toDigits(phone);
    return digits ? `tel:${digits}` : null;
}

export function buildWhatsAppLink(phone, presetMessage = '') {
    const digits = toDigits(phone);
    if (!digits) {
        return null;
    }

    let baseUrl = null;

    if (digits.length === 10) {
        baseUrl = `https://wa.me/54${digits}`;
    } else if (digits.length === 11 && digits.startsWith('0')) {
        baseUrl = `https://wa.me/54${digits.slice(1)}`;
    } else if (digits.length === 12 && digits.startsWith('54')) {
        baseUrl = `https://wa.me/${digits}`;
    } else {
        baseUrl = `https://wa.me/${digits}`;
    }

    const message = String(presetMessage || '').trim();
    if (!message) {
        return baseUrl;
    }

    return `${baseUrl}?text=${encodeURIComponent(message)}`;
}

export function buildMailtoLink(email) {
    const value = String(email || '').trim();
    if (!value) {
        return null;
    }

    return `mailto:${value}`;
}

export function getVehiculosPatentes(visita) {
    if (Array.isArray(visita?.vehiculos_detalle) && visita.vehiculos_detalle.length > 0) {
        return visita.vehiculos_detalle
            .map((vehiculo) => vehiculo?.patente)
            .filter(Boolean);
    }

    if (Array.isArray(visita?.vehiculos)) {
        return visita.vehiculos
            .map((vehiculo) => {
                if (typeof vehiculo === 'string') {
                    return vehiculo;
                }

                return vehiculo?.patente;
            })
            .filter(Boolean);
    }

    return [];
}

export function formatVehiculosListado(visita) {
    const detalleDesdeBackend = Array.isArray(visita?.vehiculos_detalle)
        ? visita.vehiculos_detalle
        : [];

    const detalleDesdeVehiculos = Array.isArray(visita?.vehiculos)
        ? visita.vehiculos
            .map((vehiculo) => {
                if (!vehiculo) {
                    return null;
                }

                if (typeof vehiculo === 'string') {
                    return { patente: vehiculo, numero_interno: null };
                }

                return {
                    patente: vehiculo?.patente || null,
                    numero_interno: vehiculo?.numero_interno || null,
                };
            })
            .filter(Boolean)
        : [];

    const detalle = detalleDesdeBackend.length > 0
        ? detalleDesdeBackend
        : detalleDesdeVehiculos;

    if (detalle.length > 0) {
        const items = detalle
            .map((vehiculo) => {
                const patente = vehiculo?.patente || '-';
                const numeroInterno = vehiculo?.numero_interno || '-';
                return `${patente} (N° ${numeroInterno})`;
            })
            .filter(Boolean);

        if (items.length === 0) {
            return 'Sin vehículos asignados';
        }

        return items.length === 1
            ? `Vehículo: ${items[0]}`
            : `Vehículos: ${items.join(', ')}`;
    }

    const patentes = getVehiculosPatentes(visita);
    if (patentes.length === 0) {
        return 'Sin vehículos asignados';
    }

    return patentes.length === 1
        ? `Vehículo: ${patentes[0]} (N° -)`
        : `Vehículos: ${patentes.map((patente) => `${patente} (N° -)`).join(', ')}`;
}

function readFormTemplateIds(value) {
    let ids = value;
    if (typeof ids === 'string') {
        try {
            ids = JSON.parse(ids);
        } catch {
            return null;
        }
    }
    if (!Array.isArray(ids)) return null;
    if (ids.some((id) => !['number', 'string'].includes(typeof id)
        || !Number.isSafeInteger(Number(id)) || Number(id) <= 0)) return null;
    return [...new Set(ids.map(Number))];
}

export function validateStartVisitaFormularios(visita) {
    const assigned = visita?.form_template_ids;
    if (assigned == null || assigned === '') {
        return ['Debe asignar al menos un formulario a la visita antes de iniciarla.'];
    }
    const ids = readFormTemplateIds(assigned);
    if (!ids) {
        return ['La asignación de formularios no es válida. Solicitá su corrección antes de iniciar la visita.'];
    }
    if (ids.length === 0) {
        return ['Debe asignar al menos un formulario a la visita antes de iniciarla.'];
    }
    // La vigencia proviene del último listado, no de los nombres ni del tipo de servicio.
    const validIds = readFormTemplateIds(visita?.form_template_ids_validos);
    if (!validIds) {
        return ['No se pudo verificar la vigencia de los formularios. Actualizá las visitas con conexión antes de iniciar.'];
    }
    if (ids.some((id) => !validIds.includes(id))) {
        return ['Hay formularios asignados inexistentes o dados de baja. Solicitá corregir la asignación y actualizá las visitas antes de iniciar.'];
    }
    return [];
}

export function validateStartVisitaRequirements(visita, { canStartPast = false, observacion = '' } = {}) {
    const errores = validateStartVisitaFormularios(visita);

    const fechaProgramadaKey = getBusinessDateKey(visita?.scheduled_at);
    if (!fechaProgramadaKey) {
        errores.push('Debe seleccionar una fecha programada.');
        return errores;
    }

    const hoyKey = formatDateKeyInBusinessTz(new Date());
    if (!hoyKey) {
        errores.push('No se pudo validar la fecha actual.');
        return errores;
    }

    if (compareDateKeys(fechaProgramadaKey, hoyKey) > 0) {
        errores.push('No se puede iniciar una visita con fecha programada futura.');
    }

    if (compareDateKeys(fechaProgramadaKey, hoyKey) < 0) {
        if (!canStartPast) {
            errores.push('No tiene permiso para iniciar visitas con fecha anterior a hoy.');
        }

        if (!String(observacion || '').trim()) {
            errores.push('Debe ingresar un motivo para iniciar una visita con fecha anterior a hoy.');
        }
    }

    const esCargaInventario = Boolean(visita?.es_carga_inventario);
    if (!esCargaInventario) {
        if (!visita?.coordinada) {
            errores.push('La visita debe estar coordinada antes de iniciarla.');
        }

        const operadoresAsignados = Array.isArray(visita?.operadores)
            ? visita.operadores.filter(Boolean).length
            : 0;

        if (operadoresAsignados < 1) {
            errores.push('Debe asignar al menos 1 operador para iniciar la visita.');
        }

        const vehiculosRequeridos = Number(visita?.vehicles_required || 0);
        const vehiculosAsignados = Array.isArray(visita?.vehiculos_detalle)
            ? visita.vehiculos_detalle.length
            : (Array.isArray(visita?.vehiculos) ? visita.vehiculos.filter(Boolean).length : 0);

        if (vehiculosRequeridos > 0 && vehiculosAsignados < 1) {
            errores.push('Debe asignar al menos 1 vehículo para iniciar la visita.');
        }
    }

    return errores;
}
